import { Controller } from "stimulus";
import { Turbo } from "@hotwired/turbo-rails";

export default class extends Controller {
  static targets = ["providerSelect", "modelInput", "chunkProvider", "chunkModel", "dictionaryRulesBody", "dictionaryRowTemplate"];

  connect() {
    this.syncProviderAndModelToChunks();
    this.refreshChunkButtons();
    this.startPolling();
  }

  disconnect() {
    this.stopPolling();
    this.cleanupPreviewAudio();
  }

  async poll() {
    if (this.hasUnsavedAudioText()) return;

    const pollUrl = this.currentElement()?.dataset.articleAudioManagerStatusUrl;
    if (!pollUrl) return;

    const requestUrl = new URL(pollUrl, window.location.origin);
    requestUrl.searchParams.set("_ts", Date.now().toString());

    try {
      const response = await fetch(requestUrl.toString(), {
        headers: {
          Accept: "text/vnd.turbo-stream.html"
        },
        cache: "no-store",
        credentials: "same-origin"
      });

      if (!response.ok) return;

      const responseText = await response.text();
      if (responseText && !this.hasUnsavedAudioText()) Turbo.renderStreamMessage(responseText);
    } catch (_error) {
    }
  }

  startPolling() {
    if (!this.pollingEnabled()) return;

    this.stopPolling();
    this.poll();

    const interval = Number(this.currentElement()?.dataset.articleAudioManagerPollingInterval || 5000);
    this.pollingTimer = setInterval(() => {
      if (!this.pollingEnabled()) {
        this.stopPolling();
        return;
      }

      this.poll();
    }, interval);
  }

  stopPolling() {
    if (!this.pollingTimer) return;

    clearInterval(this.pollingTimer);
    this.pollingTimer = null;
  }

  syncProviderAndModelToChunks() {
    const provider = this.hasProviderSelectTarget ? this.providerSelectTarget.value : null;
    const model = this.hasModelInputTarget ? this.modelInputTarget.value : null;

    if (provider) {
      this.chunkProviderTargets?.forEach((input) => { if (input) input.value = provider; });
    }

    if (model) {
      this.chunkModelTargets?.forEach((input) => { if (input) input.value = model; });
    }
  }

  refreshChunkRow(event) {
    const row = event.target.closest("[data-audio-chunk-row]");
    if (!row) return;

    this.refreshChunkButton(row);
  }

  refreshChunkButtons() {
    this.element.querySelectorAll("[data-audio-chunk-row]").forEach((row) => this.refreshChunkButton(row));
  }

  async submitChunk(event) {
    event.preventDefault();

    const form = event.target;
    const row = form.closest("[data-audio-chunk-row]");
    const button = row?.querySelector("[data-audio-chunk-submit]");

    if (!form || !row || !button || button.disabled) return;
    if (!form.reportValidity()) {
      this.refreshChunkButton(row);
      return;
    }

    button.disabled = true;

    try {
      const response = await fetch(form.action, {
        method: (form.method || "post").toUpperCase(),
        body: new FormData(form),
        headers: {
          Accept: "text/vnd.turbo-stream.html"
        },
        credentials: "same-origin"
      });

      if (!response.ok) {
        this.refreshChunkButton(row);
        return;
      }

      const responseText = await response.text();
      if (responseText) Turbo.renderStreamMessage(responseText);
      this.schedulePollingRefresh();
    } catch (_error) {
      this.refreshChunkButton(row);
    }
  }

  confirmDictionaryUpdate(event) {
    const form = event.target;
    const submitter = event.submitter;
    if (!form || form.dataset.pronunciationDictionaryConfirmed === "true") return;
    if (!this.dictionaryUpdateTouchesExistingRule(form)) return;

    event.preventDefault();

    const message = form.dataset.pronunciationDictionaryConfirmMessage;
    if (window.confirm(message)) {
      form.dataset.pronunciationDictionaryConfirmed = "true";
      window.setTimeout(() => form.requestSubmit(submitter), 0);
      return;
    }

    this.scheduleDictionarySubmitButtonEnable(form, submitter);
  }

  addDictionaryRow() {
    if (!this.hasDictionaryRulesBodyTarget || !this.hasDictionaryRowTemplateTarget) return;

    const index = this.nextDictionaryRuleIndex();
    const rowMarkup = this.dictionaryRowTemplateTarget.innerHTML.replace(/__INDEX__/g, index);

    this.dictionaryRulesBodyTarget.insertAdjacentHTML("beforeend", rowMarkup);
  }

  refreshChunkButton(row) {
    const textarea = row.querySelector("[data-audio-chunk-input]");
    const button = row.querySelector("[data-audio-chunk-submit]");
    if (!textarea || !button) return;

    const processing = row.dataset.audioChunkProcessing === "true";
    const fullOperationInProgress = row.dataset.audioChunkFullOperationInProgress === "true";
    const initialDisabled = row.dataset.audioChunkInitialDisabled === "true";
    const originalText = row.dataset.audioChunkOriginalText || "";
    const audioTextChanged = textarea.value !== originalText;
    const audioTextPresent = textarea.value.trim().length > 0;
    button.disabled = !audioTextPresent || processing || fullOperationInProgress || (initialDisabled && !audioTextChanged);
  }

  dictionaryUpdateTouchesExistingRule(form) {
    const existingRuleInputs = Array.from(form.querySelectorAll("[data-pronunciation-dictionary-existing-rule='true']"));
    const groupedInputs = existingRuleInputs.reduce((groups, input) => {
      const name = input.name || "";
      const match = name.match(/\[rules\]\[(\d+)\]/);
      if (!match) return groups;

      const index = match[1];
      groups[index] = groups[index] || [];
      groups[index].push(input);
      return groups;
    }, {});

    return Object.values(groupedInputs).some((inputs) => {
      const destroyInput = inputs.find((input) => input.dataset.pronunciationDictionaryField === "_destroy");
      if (destroyInput?.checked) return true;

      return inputs.some((input) => {
        if (input.dataset.pronunciationDictionaryField === "_destroy") return false;
        return input.value !== (input.dataset.pronunciationDictionaryOriginalValue || "");
      });
    });
  }

  enableDictionarySubmitButton(form, submitter) {
    if (submitter) {
      submitter.disabled = false;
      return;
    }

    const button = form.querySelector("input[type='submit'], button[type='submit']");
    if (button) button.disabled = false;
  }

  scheduleDictionarySubmitButtonEnable(form, submitter) {
    window.setTimeout(() => this.enableDictionarySubmitButton(form, submitter), 0);
  }

  async previewDictionaryAlias(event) {
    event.preventDefault();

    const button = event.currentTarget;
    const form = button?.closest("form");
    const row = button?.closest("tr");
    const aliasInput = row?.querySelector("input[name*='[alias]']");
    const previewUrl = form?.dataset.pronunciationDictionaryPreviewUrl;

    if (!button || !form || !aliasInput || !previewUrl) return;

    button.disabled = true;

    try {
      const formData = new FormData();
      formData.append("article_audio_pronunciation_dictionary[preview_alias]", aliasInput.value);
      formData.append("selection[provider]", this.hasProviderSelectTarget ? this.providerSelectTarget.value : "");
      formData.append("selection[model]", this.hasModelInputTarget ? this.modelInputTarget.value : "");

      const response = await fetch(previewUrl, {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: {
          "X-CSRF-Token": this.csrfToken()
        }
      });

      if (!response.ok) {
        throw new Error(await response.text() || "Preview failed");
      }

      const audioBlob = await response.blob();
      this.playPreviewAudio(audioBlob);
    } catch (error) {
      window.alert(error.message || "Preview failed");
    } finally {
      button.disabled = false;
    }
  }

  nextDictionaryRuleIndex() {
    const currentValue = Number(this.dictionaryRulesBodyTarget.dataset.pronunciationDictionaryNextIndex || 0);
    this.dictionaryRulesBodyTarget.dataset.pronunciationDictionaryNextIndex = (currentValue + 1).toString();
    return currentValue.toString();
  }

  currentElement() {
    if (this.element.isConnected) return this.element;

    return document.getElementById(this.element.id);
  }

  schedulePollingRefresh() {
    const schedule = window.requestAnimationFrame || ((callback) => window.setTimeout(callback, 0));

    schedule(() => {
      this.startPolling();
    });
  }

  pollingEnabled() {
    const element = this.currentElement();
    if (!element) return false;

    const rows = Array.from(element.querySelectorAll("[data-audio-chunk-row]"));
    const chunkOrFullOperationInProgress = rows.some((row) => (
      row.dataset.audioChunkProcessing === "true" ||
      row.dataset.audioChunkFullOperationInProgress === "true"
    ));
    const previewGenerationInProgress = element.querySelector("[data-audio-manager-preview-generation-in-progress]")?.dataset.audioManagerPreviewGenerationInProgress === "true";

    return chunkOrFullOperationInProgress || previewGenerationInProgress;
  }

  hasUnsavedAudioText() {
    return Array.from(this.element.querySelectorAll("[data-audio-chunk-input]")).some((input) => {
      const row = input.closest("[data-audio-chunk-row]");
      return row && input.value !== (row.dataset.audioChunkOriginalText || "");
    });
  }

  playPreviewAudio(blob) {
    this.cleanupPreviewAudio();

    this.previewAudioUrl = window.URL.createObjectURL(blob);
    this.previewAudio = new Audio(this.previewAudioUrl);
    this.previewAudio.addEventListener("ended", () => this.cleanupPreviewAudio(), { once: true });
    this.previewAudio.play();
  }

  cleanupPreviewAudio() {
    if (this.previewAudio) {
      this.previewAudio.pause();
      this.previewAudio = null;
    }

    if (this.previewAudioUrl) {
      window.URL.revokeObjectURL(this.previewAudioUrl);
      this.previewAudioUrl = null;
    }
  }

  csrfToken() {
    return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || "";
  }
}
